import * as React from "react"
import { useNavigate, useLocation, Link } from "react-router-dom"
import {
  Home,
  Calendar,
  FileBox,
  ChevronRight,
  Clock,
  Users,
  Mail,
  LifeBuoy,
  CalendarClock,
  BookOpen,
  Scale,
  Bot,
  Settings2,
  Map as MapIcon,
} from "lucide-react"

import { useAuth } from "@/contexts/AuthContext"
import { useLocalStorage } from "@/hooks/use-local-storage"
import { NavUser } from "./NavUser"
import { municipalitiesService } from "@/services/municipalities.service"
import { assessmentCyclesService } from "@/services/assessment-cycles.service"
import { getAssistantEnabled } from "@/services/assistant-status.service"
import { isRequestCancelled } from "@/lib/api-client"
import type { MunicipalitySummary, AssessmentCycleSummary } from "@/types/domain.types"
import { toast } from "sonner"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/sidebar"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// Strict: gates which URL segments may become selectedMunicipalityId
// (writer guard — only path-safe [\w-]+ segments).
const MUNICIPALITY_ID_URL_RE = /^\/m\/([\w-]+)(?:\/|$)/
// Loose: "does the URL claim a municipality context at all" — used only to
// suppress dropdown pre-selection. Deliberately looser than the writer
// guard so a malformed /m/<junk> URL doesn't get a municipality silently
// pre-selected under it.
const MUNICIPALITY_CONTEXT_RE = /^\/m\/[^/]/

interface GatedNavItemProps {
  icon: React.ComponentType<{ className?: string }>
  label: string
  selectedMunicipalityId: string | null
  href: string
  isActive: boolean
}

/**
 * A top-level nav item gated on a municipality being selected: a real
 * `asChild` + `<Link>` when one is, a disabled `aria-disabled` button (no
 * link semantics — there's nowhere to link to yet) when none is. Shared by
 * Emails/Map Data/Templates so their gating, tooltip copy, and classes
 * can't silently drift apart across near-identical copies. `isActive`
 * stays a caller-supplied prop (rather than being computed in here) so
 * each item's own matching rule (exact-match for Emails, prefix-match for
 * Map Data/Templates) is explicit and independently testable at the call
 * site.
 */
function GatedNavItem({ icon: Icon, label, selectedMunicipalityId, href, isActive }: GatedNavItemProps) {
  if (!selectedMunicipalityId) {
    return (
      <SidebarMenuButton
        tooltip={`${label} - Select a municipality first`}
        disabled
        aria-disabled
        className="h-10 px-3 font-medium transition-all duration-200 text-muted-foreground cursor-not-allowed opacity-50"
      >
        <Icon className="h-4 w-4" />
        <span className="text-sm">{label}</span>
      </SidebarMenuButton>
    )
  }
  return (
    <SidebarMenuButton
      asChild
      tooltip={label}
      isActive={isActive}
      className="h-10 px-3 font-medium transition-all duration-200 hover:bg-sidebar-accent cursor-pointer"
    >
      <Link to={href}>
        <Icon className="h-4 w-4" />
        <span className="text-sm">{label}</span>
      </Link>
    </SidebarMenuButton>
  )
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut } = useAuth()
  const { state: sidebarState } = useSidebar()
  const isCollapsed = sidebarState === "collapsed"
  const [isLoading, setIsLoading] = React.useState(true)
  const [selectedMunicipalityId, setSelectedMunicipalityId] = React.useState<string | null>(null)
  const [municipalities, setMunicipalities] = React.useState<MunicipalitySummary[]>([])
  const [municipalitiesError, setMunicipalitiesError] = React.useState(false)
  const [cycles, setCycles] = React.useState<AssessmentCycleSummary[]>([])
  const [cyclesError, setCyclesError] = React.useState(false)
  const [cyclesOpen, setCyclesOpen] = React.useState(true)
  const [assistantOpen, setAssistantOpen] = React.useState(true)
  // Org-level tenant switch. Default false = hidden (dormant-safe) until the
  // probe confirms the assistant is on for the selected muni's org.
  const [assistantEnabled, setAssistantEnabled] = React.useState(false)

  // Persist last selected municipality per user
  const storageKey = user?.uid ? `smartextract:lastMunicipality:${user.uid}` : 'smartextract:lastMunicipality'
  const [savedMunicipalityId, setSavedMunicipalityId] = useLocalStorage<string | null>(storageKey, null)

  // Extract municipality ID from URL if present and save it. Only
  // path-safe segments ([\w-]+ — no ':', '/', '%', quotes, angle brackets)
  // may become selected state — the other two writers (localStorage
  // restore, the Select) are validated against the fetched municipalities
  // list, and this guard keeps a crafted /m/<junk> URL from being
  // persisted and interpolated into every sidebar href.
  React.useEffect(() => {
    const match = location.pathname.match(MUNICIPALITY_ID_URL_RE)
    if (match) {
      const id = match[1]
      setSelectedMunicipalityId(id)
      if (user?.uid) {
        setSavedMunicipalityId(id)
      }
    }
  }, [location.pathname, user?.uid, setSavedMunicipalityId])

  // Fetch municipalities from API. Extracted as a retryable callback so a
  // failed fetch can render a Retry affordance in the selector slot instead
  // of silently leaving the sidebar selector-less (the same "error must
  // never resolve to an empty/default UI" rule applied elsewhere on this
  // branch). AbortController wiring (creation timing, cleanup, signal
  // checks) is unchanged from the original effect.
  const fetchMunicipalities = React.useCallback(async (signal?: AbortSignal) => {
    if (!user?.organizationId) return

    setIsLoading(true)
    setMunicipalitiesError(false)
    try {
      const data = await municipalitiesService.getSummaries(user.organizationId, { signal })
      if (!signal?.aborted) {
        setMunicipalities(Array.isArray(data) ? data : [])
      }
    } catch (err) {
      if (isRequestCancelled(err)) return
      if (!signal?.aborted) {
        console.error('Failed to load municipalities:', err)
        setMunicipalitiesError(true)
      }
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false)
      }
    }
  }, [user?.organizationId])

  // Retry affordance for the municipality-selector error row. Routes through
  // an abort-then-replace ref-controller (same pattern as
  // CycleDetailPage.tsx's handleRetryInitialLoad) instead of calling
  // fetchMunicipalities() bare — a bare call passes no AbortSignal, so its
  // `!signal?.aborted` guards are vacuously true and a stale retry response
  // could never be discarded. The effect below also aborts this ref on
  // cleanup/unmount so a stray in-flight retry can't outlive the component.
  const municipalitiesRetryRef = React.useRef<AbortController | null>(null)
  const handleRetryMunicipalities = React.useCallback(() => {
    municipalitiesRetryRef.current?.abort()
    const controller = new AbortController()
    municipalitiesRetryRef.current = controller
    fetchMunicipalities(controller.signal)
  }, [fetchMunicipalities])

  React.useEffect(() => {
    if (!user?.organizationId) {
      setIsLoading(false)
      return () => {
        municipalitiesRetryRef.current?.abort()
      }
    }

    const controller = new AbortController()
    fetchMunicipalities(controller.signal)
    return () => {
      controller.abort()
      municipalitiesRetryRef.current?.abort()
    }
  }, [user?.organizationId, fetchMunicipalities])

  // Pre-select saved municipality in dropdown (or first if none saved) - NO redirect
  React.useEffect(() => {
    // Wait for user auth and municipalities to load
    if (isLoading || municipalities.length === 0 || !user?.uid) return

    // If URL has a municipality, use that (already handled by URL effect)
    if (MUNICIPALITY_CONTEXT_RE.test(location.pathname)) return

    // Otherwise pre-select saved or first (just the dropdown, no navigation)
    if (!selectedMunicipalityId) {
      const targetId = savedMunicipalityId && municipalities.some(m => m.id === savedMunicipalityId)
        ? savedMunicipalityId
        : municipalities[0].id
      setSelectedMunicipalityId(targetId)
    }
  }, [isLoading, municipalities, location.pathname, selectedMunicipalityId, savedMunicipalityId, user?.uid])

  // Fetch cycles when municipality is selected. Extracted as a retryable
  // callback so a failed fetch can render a Retry row under Cycles instead
  // of silently rendering a cycle-less nav (same rule as municipalities,
  // above). AbortController wiring is unchanged from the original effect.
  const fetchCycles = React.useCallback(async (signal?: AbortSignal) => {
    if (!selectedMunicipalityId) return

    setCyclesError(false)
    try {
      const data = await assessmentCyclesService.getByMunicipality(selectedMunicipalityId, { signal })
      if (!signal?.aborted) {
        setCycles(Array.isArray(data) ? data : [])
      }
    } catch (err) {
      if (isRequestCancelled(err)) return
      if (!signal?.aborted) {
        console.error('Failed to load cycles:', err)
        setCyclesError(true)
      }
    }
  }, [selectedMunicipalityId])

  // Retry affordance for the "Couldn't load cycles" row. Same abort-then-
  // replace ref-controller pattern as handleRetryMunicipalities above (and
  // CycleDetailPage.tsx's handleRetryInitialLoad) — a bare fetchCycles()
  // call passes no AbortSignal, so a stale retry response could otherwise
  // never be discarded and could paint one municipality's cycles under
  // another. The effect below aborts this ref on cleanup so switching
  // municipalities cancels any in-flight retry too.
  const cyclesRetryRef = React.useRef<AbortController | null>(null)
  const handleRetryCycles = React.useCallback(() => {
    cyclesRetryRef.current?.abort()
    const controller = new AbortController()
    cyclesRetryRef.current = controller
    fetchCycles(controller.signal)
  }, [fetchCycles])

  React.useEffect(() => {
    // Clear stale cycles immediately on every municipality change (including
    // to none) — not just in fetchCycles' catch branch — so municipality A's
    // cycles never render as live links (with hrefs pointing at A's cycle
    // ids under B's URL) during the in-flight window before B's fetch
    // settles, or while a Retry for B is still pending.
    setCycles([])

    if (!selectedMunicipalityId) {
      setCyclesError(false)
      return () => {
        cyclesRetryRef.current?.abort()
      }
    }

    const controller = new AbortController()
    fetchCycles(controller.signal)
    return () => {
      controller.abort()
      cyclesRetryRef.current?.abort()
    }
  }, [selectedMunicipalityId, fetchCycles])

  // Probe the org tenant switch for the selected muni; hide the Assistant
  // nav group when off (and while loading / on error) so a dormant tenant
  // never sees the assistant surfaces.
  React.useEffect(() => {
    if (!selectedMunicipalityId) {
      setAssistantEnabled(false)
      return
    }
    const controller = new AbortController()
    getAssistantEnabled(selectedMunicipalityId, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setAssistantEnabled(!!res.enabled)
      })
      .catch((err) => {
        if (err instanceof Error && err.name === 'CanceledError') return
        if (!controller.signal.aborted) setAssistantEnabled(false)
      })
    return () => controller.abort()
  }, [selectedMunicipalityId])

  const handleSignOut = async () => {
    try {
      await signOut()
      toast.success("Logged out successfully")
      navigate("/login")
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to log out"
      toast.error(errorMessage)
    }
  }

  // Handle municipality selection
  const handleMunicipalityChange = (municipalityId: string) => {
    setSelectedMunicipalityId(municipalityId)
    setSavedMunicipalityId(municipalityId) // Persist selection
    navigate(`/m/${municipalityId}`)
  }

  // Get user display info
  const getUserInfo = () => {
    return {
      name: user?.displayName || "User",
      email: user?.email || "user@example.com",
      avatar: user?.photoURL || "",
    }
  }

  return (
    <Sidebar
      collapsible="offcanvas"
      className="shadow-lg md:shadow-none border-r border-sidebar-border"
      {...props}
    >
      <SidebarHeader className="border-b border-sidebar-border p-4">
        {/* Logo - Clickable to navigate to dashboard */}
        <div className="flex items-center gap-2">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 rounded-lg hover:bg-sidebar-accent transition-all duration-200 p-2 -m-2"
            title="Go to Dashboard"
          >
            {isLoading ? (
              <div className="h-8 w-8 rounded animate-pulse bg-sidebar-accent/20" />
            ) : (
              <img src="/logo.svg" alt="" className="h-8 w-8 flex-shrink-0" />
            )}
            {!isCollapsed && (
              <span className="font-mono text-base font-semibold whitespace-nowrap"><span className="text-muted-foreground">Smart</span>Extract</span>
            )}
          </Link>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-3 py-4 flex-1 overflow-y-auto">
        {/* Municipality Selector - only show when expanded. On fetch
            failure, render a compact inline error row in this slot instead
            of silently leaving the sidebar selector-less. */}
        {!isCollapsed && municipalitiesError && (
          <SidebarGroup className="mb-4 pb-3 border-b border-sidebar-border">
            <div className="flex items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
              <span className="truncate">Couldn't load municipalities</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 shrink-0 px-2 text-xs"
                onClick={handleRetryMunicipalities}
              >
                Retry
              </Button>
            </div>
          </SidebarGroup>
        )}
        {!isCollapsed && !municipalitiesError && municipalities.length > 0 && (
          <SidebarGroup className="mb-4 pb-3 border-b border-sidebar-border">
            <Select
              value={selectedMunicipalityId || ""}
              onValueChange={handleMunicipalityChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select municipality..." />
              </SelectTrigger>
              <SelectContent>
                {municipalities.map((municipality) => (
                  <SelectItem key={municipality.id} value={municipality.id}>
                    {municipality.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SidebarGroup>
        )}

        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarMenu className="space-y-1">
            {/* Dashboard */}
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip="Dashboard"
                isActive={location.pathname === "/dashboard"}
                className="h-10 px-3 font-medium transition-all duration-200 hover:bg-sidebar-accent cursor-pointer"
              >
                <Link to="/dashboard">
                  <Home className="h-4 w-4" />
                  <span className="text-sm">Dashboard</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>

            {/* Emails */}
            <SidebarMenuItem>
              <GatedNavItem
                icon={Mail}
                label="Emails"
                selectedMunicipalityId={selectedMunicipalityId}
                href={`/m/${selectedMunicipalityId}/emails`}
                isActive={location.pathname === `/m/${selectedMunicipalityId}/emails`}
              />
            </SidebarMenuItem>

            {/* Cycles - Collapsible with sub-items */}
            <Collapsible open={cyclesOpen} onOpenChange={setCyclesOpen} className="group/collapsible">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={!selectedMunicipalityId ? "Cycles - Select a municipality first" : "Cycles"}
                    disabled={!selectedMunicipalityId}
                    aria-disabled={!selectedMunicipalityId}
                    className={`h-10 px-3 font-medium transition-all duration-200 ${
                      !selectedMunicipalityId
                        ? "text-muted-foreground cursor-not-allowed opacity-50"
                        : "hover:bg-sidebar-accent cursor-pointer"
                    }`}
                  >
                    <Calendar className="h-4 w-4" />
                    <span className="text-sm">Cycles</span>
                    {selectedMunicipalityId && (
                      <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    )}
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                {/* Gated on selectedMunicipalityId — same rule as the
                    Assistant group below. Without this gate, "All" would
                    render as a real, focusable, clickable `href="/m/null/cycles"`
                    anchor whenever no municipality is selected (cyclesOpen
                    defaults to true), since asChild+Link navigates on real
                    browser click/middle-click/Cmd-click regardless of any
                    disabled state on a sibling trigger. */}
                {selectedMunicipalityId && (
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {/* All link - goes to cycles dashboard */}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname === `/m/${selectedMunicipalityId}/cycles`}
                          className="cursor-pointer"
                        >
                          <Link to={`/m/${selectedMunicipalityId}/cycles`}>
                            <span>All</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      {/* Individual cycles */}
                      {cycles.map((cycle) => {
                        const cycleUrl = `/m/${selectedMunicipalityId}/cycles/${cycle.id}`
                        const isActive = location.pathname === cycleUrl || location.pathname.startsWith(cycleUrl + "/")
                        const pendingCount = cycle.pendingCount ?? 0

                        return (
                          <SidebarMenuSubItem key={cycle.id}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={isActive}
                              className={`cursor-pointer ${cycle.status === "archived" ? "opacity-50" : ""}`}
                            >
                              <Link to={cycleUrl}>
                                {cycle.status === "active" && (
                                  <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" />
                                )}
                                {cycle.status === "draft" && (
                                  <span className="h-2 w-2 rounded-full bg-slate-400 shrink-0" />
                                )}
                                {cycle.status === "archived" && (
                                  <span className="h-2 w-2 rounded-full bg-slate-400 shrink-0" />
                                )}
                                <span className="truncate">{cycle.name}</span>
                                {pendingCount > 0 && (
                                  <Badge className="ml-auto h-5 px-1.5 text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100">
                                    <Clock className="h-3 w-3 mr-0.5" />
                                    {pendingCount}
                                  </Badge>
                                )}
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        )
                      })}
                      {/* Cycles fetch failed - render a Retry row instead of
                          silently showing just the "All" link with no cycles.
                          Rendered via asChild + a real <button> (not a link —
                          this is a retry action, not navigation) so it is
                          keyboard-focusable, matching every other interactive
                          item in this file. */}
                      {cyclesError && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            className="cursor-pointer text-destructive hover:text-destructive"
                          >
                            <button type="button" onClick={handleRetryCycles}>
                              <span className="truncate">Couldn't load cycles — Retry</span>
                            </button>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                )}
              </SidebarMenuItem>
            </Collapsible>

            {/* Map Data */}
            <SidebarMenuItem>
              <GatedNavItem
                icon={MapIcon}
                label="Map Data"
                selectedMunicipalityId={selectedMunicipalityId}
                href={`/m/${selectedMunicipalityId}/map-data`}
                isActive={location.pathname.startsWith(`/m/${selectedMunicipalityId}/map-data`)}
              />
            </SidebarMenuItem>

            {/* Templates */}
            <SidebarMenuItem>
              <GatedNavItem
                icon={FileBox}
                label="Templates"
                selectedMunicipalityId={selectedMunicipalityId}
                href={`/m/${selectedMunicipalityId}/templates`}
                isActive={location.pathname.startsWith(`/m/${selectedMunicipalityId}/templates`)}
              />
            </SidebarMenuItem>

            {/* Team - Admin only */}
            {user?.role === "admin" && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip="Team"
                  isActive={location.pathname === "/users"}
                  className="h-10 px-3 font-medium transition-all duration-200 hover:bg-sidebar-accent cursor-pointer"
                >
                  <Link to="/users">
                    <Users className="h-4 w-4" />
                    <span className="text-sm">Team</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {/* Assistant - Collapsible group for all owner-assistant admin
                surfaces. Calendar is for any authenticated user (links their
                own Microsoft 365 calendar); Knowledge/Comparables/Escalations
                are admin-only. Sub-item order mirrors the onboarding flow:
                data setup first, operational triage last.
                Gate: only rendered when the org-level tenant switch is on. */}
            {assistantEnabled && (
            <Collapsible open={assistantOpen} onOpenChange={setAssistantOpen} className="group/collapsible">
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={!selectedMunicipalityId ? "Assistant - Select a municipality first" : "Assistant"}
                    disabled={!selectedMunicipalityId}
                    aria-disabled={!selectedMunicipalityId}
                    className={`h-10 px-3 font-medium transition-all duration-200 ${
                      !selectedMunicipalityId
                        ? "text-muted-foreground cursor-not-allowed opacity-50"
                        : "hover:bg-sidebar-accent cursor-pointer"
                    }`}
                  >
                    <Bot className="h-4 w-4" />
                    <span className="text-sm">Assistant</span>
                    {selectedMunicipalityId && (
                      <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    )}
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                {selectedMunicipalityId && (
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {(user?.role === "admin" || user?.role === "super_admin") && (
                        <>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={location.pathname === `/m/${selectedMunicipalityId}/assistant-knowledge`}
                              className="cursor-pointer"
                            >
                              <Link to={`/m/${selectedMunicipalityId}/assistant-knowledge`}>
                                <BookOpen className="h-4 w-4" />
                                <span>Knowledge</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={location.pathname === `/m/${selectedMunicipalityId}/assistant-comparables`}
                              className="cursor-pointer"
                            >
                              <Link to={`/m/${selectedMunicipalityId}/assistant-comparables`}>
                                <Scale className="h-4 w-4" />
                                <span>Comparables</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </>
                      )}
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={location.pathname.startsWith(`/m/${selectedMunicipalityId}/assessor/calendar`)}
                          className="cursor-pointer"
                        >
                          <Link to={`/m/${selectedMunicipalityId}/assessor/calendar`}>
                            <CalendarClock className="h-4 w-4" />
                            <span>Calendar</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                      {(user?.role === "admin" || user?.role === "super_admin") && (
                        <>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={location.pathname === `/m/${selectedMunicipalityId}/assistant-escalations`}
                              className="cursor-pointer"
                            >
                              <Link to={`/m/${selectedMunicipalityId}/assistant-escalations`}>
                                <LifeBuoy className="h-4 w-4" />
                                <span>Escalations</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={location.pathname === `/m/${selectedMunicipalityId}/assistant-settings`}
                              className="cursor-pointer"
                            >
                              <Link to={`/m/${selectedMunicipalityId}/assistant-settings`}>
                                <Settings2 className="h-4 w-4" />
                                <span>Settings</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        </>
                      )}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                )}
              </SidebarMenuItem>
            </Collapsible>
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-3 flex-shrink-0">
        {/* User Profile */}
        <NavUser user={getUserInfo()} onSignOut={handleSignOut} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
